'use client';

import { useEffect, useRef } from 'react';
import { getPerformanceLabLenis } from 'utils/performanceLabLenisRegistry';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Sets `data-inview="true"` on the element the first time it scrolls into view;
 * CSS owns the actual transition. Reduced motion: marked in view immediately.
 */
export const useRevealOnce = <T extends HTMLElement>(
  rootMargin = '0px 0px -12% 0px',
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      el.dataset.inview = 'true';
      return undefined;
    }
    el.dataset.inview = 'false';
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.dataset.inview = 'true';
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return ref;
};

/**
 * Writes `--scroll` (−1 → 1) on the element: −1 as it enters at the viewport
 * bottom, 0 when centred, 1 as it leaves the top. Drives light parallax in CSS
 * without React re-renders. Reduced motion: pinned at 0.
 */
export const useScrollProgress = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReducedMotion()) {
      el.style.setProperty('--scroll', '0');
      return undefined;
    }
    let raf = 0;
    const update = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress =
        (vh / 2 - (r.top + r.height / 2)) / ((vh + r.height) / 2);
      el.style.setProperty(
        '--scroll',
        Math.max(-1, Math.min(1, progress)).toFixed(4),
      );
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    getPerformanceLabLenis()?.on?.('scroll', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      getPerformanceLabLenis()?.off?.('scroll', onScroll);
    };
  }, []);

  return ref;
};
